// ============================================================================
// BESPOKIAN Pharmacist Cockpit - Queue Stats Bar
// Real-time queue status indicators
// ============================================================================

import { useQuery } from '@tanstack/react-query';
import { clsx } from 'clsx';
import { ClipboardCheck, AlertTriangle, Package, CheckCircle, Truck } from 'lucide-react';
import { queueApi } from '@/api/queue.api';
import { useAuth } from '@/contexts/AuthContext';

interface StatItem {
  key: string;
  label: string;
  icon: typeof ClipboardCheck;
  color: string;
  bgColor: string;
}

const STAT_ITEMS: StatItem[] = [
  {
    key: 'PENDING_VERIFICATION',
    label: 'Verify',
    icon: ClipboardCheck,
    color: 'text-amber-600',
    bgColor: 'bg-amber-100',
  },
  {
    key: 'PENDING_DUR',
    label: 'DUR',
    icon: AlertTriangle,
    color: 'text-orange-600',
    bgColor: 'bg-orange-100',
  },
  {
    key: 'PENDING_FILL',
    label: 'Fill',
    icon: Package,
    color: 'text-purple-600',
    bgColor: 'bg-purple-100',
  },
  {
    key: 'PENDING_PHARMACIST_CHECK',
    label: 'Check',
    icon: CheckCircle,
    color: 'text-teal-600',
    bgColor: 'bg-teal-100',
  },
  {
    key: 'READY_FOR_PICKUP',
    label: 'Ready',
    icon: Truck,
    color: 'text-green-600',
    bgColor: 'bg-green-100',
  },
];

export function QueueStats() {
  const { isAuthenticated } = useAuth();
  const { data: counts, isLoading } = useQuery({
    queryKey: ['queue', 'counts'],
    queryFn: () => queueApi.getQueueCounts(),
    enabled: isAuthenticated,
    refetchInterval: isAuthenticated ? 10000 : false,
  });

  const totalCount = counts
    ? Object.entries(counts)
        .filter(([key]) => key !== '')
        .reduce((sum, [, count]) => sum + count, 0)
    : 0;

  return (
    <div className="h-12 bg-slate-100 border-b border-slate-200 flex items-center px-6 gap-4">
      {/* Total Count */}
      <div className="flex items-center gap-2 pr-4 border-r border-slate-300">
        <span className="text-sm text-slate-500">Total Queue:</span>
        <span className="text-lg font-bold text-slate-900">
          {isLoading ? '...' : totalCount}
        </span>
      </div>

      {/* Individual Stats */}
      <div className="flex items-center gap-4">
        {STAT_ITEMS.map((item) => {
          const count = counts?.[item.key as keyof typeof counts] || 0;
          const Icon = item.icon;

          return (
            <div
              key={item.key}
              className="flex items-center gap-2"
              title={`${item.label}: ${count}`}
            >
              <div className={clsx('p-1 rounded', item.bgColor)}>
                <Icon className={clsx('h-4 w-4', item.color)} />
              </div>
              <span className="text-xs font-medium text-slate-600">
                {item.label}
              </span>
              <span
                className={clsx(
                  'text-sm font-bold',
                  count > 0 ? 'text-slate-900' : 'text-slate-400'
                )}
              >
                {isLoading ? '-' : count}
              </span>
            </div>
          );
        })}
      </div>

      {/* Last Updated */}
      <div className="ml-auto text-xs text-slate-400">
        Auto-refresh: 10s
      </div>
    </div>
  );
}
